/**
 * 📋 SDLC 6.5.0 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/features/attendanceModern/screens/CameraCapture.tsx
 * PURPOSE: SC-017-EMP-ATTENDANCE [3] capture_photo — camera trong app (không
 *          phải <input type="file" capture>). 2 bước con, khớp mobile
 *          PhotoCapture.tsx: (a) trước khi chụp — khung mời chụp + nút "Chụp
 *          ảnh"; (b) sau khi chụp — xem lại + "Chụp lại"/"Xác nhận". Chỉ gọi
 *          onCapture ở bước (b) khi bấm "Xác nhận" — KHÔNG watermark ở đây
 *          (watermark cần lat/lon/tên NV/token, thuộc về hook gọi component
 *          này — xem useAttendanceModernClock.ts::capturePhoto).
 *
 *          KHÔNG tái dùng frontend/src/components/PhotoCapture.tsx /
 *          PhotoVerification.tsx (dead code, mô phỏng liveness detection giả —
 *          vi phạm Zero-Mock Tolerance Rule 3) — chỉ tham khảo cách gọi
 *          getUserMedia/canvas của 2 file đó, viết lại sạch không có phần giả.
 * SDLC FRAMEWORK VERSION: 6.5.0 (7-Pillar + 9 Mental Models + Long-Running Agent Protocol)
 * DATE: 2026-09-02
 * COMPLIANCE: SDLC 6.5.0 + WIREFRAME-V0-MASTER-REGISTRY.md §SC-017-EMP-ATTENDANCE [3]
 * PILLAR: P1 (Stage 06 - BUILD) + P6 (UI/UX Design)
 * SPRINT GOVERNANCE: Rule 2: Milestone-Gated Roadmaps, Rule 3: Zero-Mock Tolerance
 *
 * @cross-references
 * - Design: docs/02-design/04-ui-ux-design/persona-flows/WIREFRAME-V0-MASTER-REGISTRY.md §SC-017-EMP-ATTENDANCE
 * - Mobile source of truth: mobile/src/screens/attendance/components/PhotoCapture.tsx
 *  */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import styles from '@/pages/AttendanceModern.module.css';

interface CameraCaptureProps {
  /** Khi true: dòng "* Bắt buộc chụp ảnh để chấm công" hiện, KHÔNG có nút Hủy
   *  (phương thức GPS ở [3]). Khi false: KHÔNG hiện dòng đó, CÓ nút Hủy cạnh
   *  nút chụp (Timemark ở [6]) — khớp ANNOTATION [3] đã ghi trong registry. */
  required: boolean;
  onCapture: (photoDataUrl: string) => void;
  onCancel?: () => void;
  testIdPrefix?: string;
}

// #1011: trước đây mọi lỗi getUserMedia ra cùng một câu, nên người dùng
// không biết camera bị chặn quyền hay không có camera. Phân loại theo
// DOMException.name (chuẩn W3C Media Capture).
export function cameraErrorMessage(err: unknown): string {
  const name = (err as { name?: string } | null)?.name;
  switch (name) {
    case 'NotAllowedError':
    case 'SecurityError':
      return 'Camera đang bị chặn. Bấm biểu tượng ổ khóa cạnh thanh địa chỉ, chọn "Cho phép" camera rồi tải lại trang. Nếu vẫn bị chặn, hãy báo quản lý.';
    case 'NotFoundError':
    case 'OverconstrainedError':
      return 'Không tìm thấy camera trên thiết bị này.';
    case 'NotReadableError':
      return 'Camera đang được ứng dụng khác sử dụng. Hãy đóng ứng dụng đó rồi thử lại.';
    case 'NotSupportedError':
      return 'Trình duyệt này không mở được camera. Hãy dùng Chrome hoặc Safari bản mới và mở trang qua https.';
    default:
      return 'Không thể mở camera. Vui lòng thử lại.';
  }
}

// navigator.mediaDevices không có (http không an toàn, trình duyệt cũ): trước
// đây `?.` làm cả chuỗi .then/.catch bị bỏ qua và màn hình kẹt ở "đang mở".
function openCamera(): Promise<MediaStream> {
  if (!navigator.mediaDevices?.getUserMedia) {
    return Promise.reject(new DOMException('mediaDevices unavailable', 'NotSupportedError'));
  }
  return navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' }, audio: false });
}

export const CameraCapture: React.FC<CameraCaptureProps> = ({
  required,
  onCapture,
  onCancel,
  testIdPrefix = 'camera-capture',
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [isStarting, setIsStarting] = useState(true);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [capturedPhoto, setCapturedPhoto] = useState<string | null>(null);

  const stopStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  }, []);

  useEffect(() => {
    let cancelled = false;
    setIsStarting(true);
    setCameraError(null);

    openCamera()
      .then((stream) => {
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
        setIsStarting(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setCameraError(cameraErrorMessage(err));
        setIsStarting(false);
      });

    return () => {
      cancelled = true;
      stopStream();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTakePhoto = useCallback(() => {
    const video = videoRef.current;
    if (!video || video.videoWidth === 0) return;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(video, 0, 0);
    setCapturedPhoto(canvas.toDataURL('image/jpeg', 0.9));
    // Camera không cần chạy tiếp trong lúc xem lại — tắt stream, mở lại nếu "Chụp lại".
    stopStream();
  }, [stopStream]);

  const handleRetake = useCallback(() => {
    setCapturedPhoto(null);
    setIsStarting(true);
    openCamera()
      .then((stream) => {
        streamRef.current = stream;
        if (videoRef.current) videoRef.current.srcObject = stream;
        setIsStarting(false);
      })
      .catch((err) => {
        setCameraError(cameraErrorMessage(err));
        setIsStarting(false);
      });
  }, []);

  if (capturedPhoto) {
    return (
      <div>
        <img src={capturedPhoto} alt="Ảnh vừa chụp" className={styles.photoImage} data-testid={`${testIdPrefix}-preview`} />
        <div className={styles.actionsRow}>
          <button type="button" className={styles.secondaryButton} onClick={handleRetake} data-testid={`${testIdPrefix}-retake`}>
            Chụp lại
          </button>
          <button
            type="button"
            className={styles.primaryButton}
            onClick={() => onCapture(capturedPhoto)}
            data-testid={`${testIdPrefix}-confirm`}
          >
            Xác nhận
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {cameraError ? (
        <p className={`${styles.statusMessage} ${styles.statusError}`} role="alert">
          {cameraError}
        </p>
      ) : (
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className={styles.photoImage}
          data-testid={`${testIdPrefix}-video`}
        />
      )}
      <div className={styles.actionsRow}>
        {!required && onCancel && (
          <button type="button" className={styles.secondaryButton} onClick={onCancel} data-testid={`${testIdPrefix}-cancel`}>
            Hủy
          </button>
        )}
        <button
          type="button"
          className={styles.primaryButton}
          onClick={handleTakePhoto}
          disabled={isStarting || !!cameraError}
          data-testid={`${testIdPrefix}-take`}
        >
          Chụp ảnh
        </button>
      </div>
      {required && (
        <p className={styles.accuracyNote} data-testid={`${testIdPrefix}-required-note`}>
          * Bắt buộc chụp ảnh để chấm công
        </p>
      )}
    </div>
  );
};

export default CameraCapture;
